import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "@store/index";
import {
  InvestorRequestState,
  Country,
  InvestorRequestPaymentMethod,
  C2DSFieldErrors,
  C2DSFieldKey,
} from "@investorRequest/models";
import { MultiselectOption, ListOptionBase } from "@shared/components";
import CommonUtils from "@shared/utils/CommonUtils";
import {
  entitledAccountsSelector,
  entitledFundsSelector,
  entitledValidEndDateFundsSelector,
} from "@store/Entities/selectors";
import { selectInternalExternalPresence } from "@store/Entities/ducks";
import { EntitledFund } from "@store/Entities/models";
import { WIRE_INSTRUCTIONS_INTERNAL_FUNDS_ALLOWLIST } from "@investorRequest/constants";
import FeatureToggleService from "@services/FeatureToggleService";
import { ToggledFeature } from "@shared/models/featureToggle";

// Selector to check if user is admin based on entitled funds' isAdmin field
const isUserAdminSelector = createSelector(entitledFundsSelector, (fundsSlice) => {
  if (!fundsSlice.data) return false;

  // User is admin if ANY of their entitled funds has isAdmin: true
  const isAdmin = Object.values(fundsSlice.data).some((fund) => fund.isAdmin === true);

  return isAdmin;
});

export const investorRequestRootSelector = (state: RootState): InvestorRequestState => state.investorRequest;

// Tab Independence: Selector to check if current tab has been initialized
export const currentTabInitializedSelector = (state: RootState): boolean => {
  const activeInfoTab = state.investorRequest.activeInfoTab;
  const currentTab = activeInfoTab === "Internal Fund Information" ? "internal" : "external";

  return state.investorRequest.tabSpecificData[currentTab].hasBeenInitialized;
};

// Selector to check if current tab has unsaved changes (for "Save as Draft" button state)
export const hasUnsavedChangesSelector = (state: RootState): boolean => {
  const activeInfoTab = state.investorRequest.activeInfoTab;
  const currentTab = activeInfoTab === "Internal Fund Information" ? "internal" : "external";

  return state.investorRequest.tabSpecificData[currentTab].hasUnsavedChanges;
};

const selectedAccountsSelector = (state: RootState): InvestorRequestState["commonFields"]["selectedAccounts"] =>
  state.investorRequest.commonFields.selectedAccounts;

const selectedFundsSelector = (state: RootState): InvestorRequestState["commonFields"]["selectedFunds"] =>
  state.investorRequest.commonFields.selectedFunds;

const activeInfoTabSelector = (state: RootState): InvestorRequestState["activeInfoTab"] =>
  state.investorRequest.activeInfoTab;

// Wire Instructions: Helper functions for fund filtering
// NO AlpInvest or Real Estate exclusions - users need to see ALL their entitled funds

/**
 * Checks if fund status is closed (fundraisingStatus = "Closed" OR entityType = "Open Ended")
 * Both checks are case-insensitive
 */
const isFundStatusClosed = (fund: EntitledFund): boolean => {
  const fundraisingStatus = (fund.fundraisingStatus || "").toLowerCase();
  const entityType = (fund.entityType || "").toLowerCase();

  return fundraisingStatus === "closed" || entityType === "open ended";
};

/**
 * Checks if an Internal fund is in the business exception allowlist
 * These funds bypass the fundraisingStatus/entityType check
 */
const isInternalFundAllowlisted = (fund: EntitledFund): boolean => {
  return WIRE_INSTRUCTIONS_INTERNAL_FUNDS_ALLOWLIST.includes(fund.globalId as any);
};

const isWireInstructionsInternalFund = (fund: EntitledFund): boolean => {
  // ONLY accept the 4 specific allowlisted internal funds
  // Business requirement: Only these funds should be considered as internal funds:
  // - Carlyle Carried Interest (F000122524)
  // - Carlyle Equity Pool (F000122525)
  // - Carlyle Internal Coinvestment (F000122526)
  // - Carlyle Holdings Units (F000122523)
  // All other funds (even if processArea=Internal with closed status) are excluded

  // Defensive checks: Even for allowlisted funds, verify basic criteria
  // to ensure data integrity
  if (!isInternalFundAllowlisted(fund)) {
    return false;
  }

  // Verify the allowlisted fund still meets basic requirements
  if (fund.processArea !== "Internal" || !fund.includeInDmltFeed) {
    return false;
  }

  return true;
};

const isWireInstructionsExternalFund = (fund: EntitledFund): boolean => {
  const processArea = String(fund.processArea || "")
    .trim()
    .toLowerCase();

  // Base criteria: must be External and included in DMLT feed
  if (!processArea.startsWith("external") || !fund.includeInDmltFeed) {
    return false;
  }

  // External funds must have closed status (no allowlist exception)
  return isFundStatusClosed(fund);
};

// Wire Instructions: Filtered accounts selector based on user type and active tab
// NO AlpInvest/Real Estate exclusions - filter only by processArea (Internal/External)
const wireInstructionsAccountsSelector = createSelector(
  entitledAccountsSelector,
  entitledFundsSelector,
  selectedAccountsSelector,
  activeInfoTabSelector,
  (state: RootState) => selectInternalExternalPresence(state as any),
  isUserAdminSelector,
  (accountsSlice, fundsSlice, selectedAccounts, activeInfoTab, { hasInternal, hasExternal }, isAdmin) => {
    const selectedAccountsMap = CommonUtils.convertArrayToMap(selectedAccounts);

    if (!accountsSlice.data) return [];

    const isHybrid = hasInternal && hasExternal;

    // Admin users see ALL accounts (no filtering)
    if (isAdmin) {
      const accounts = CommonUtils.convertMapToArray(
        accountsSlice.data,
        (account): MultiselectOption => ({
          id: account.globalId,
          label: account.name,
          selected: selectedAccountsMap[account.globalId],
        })
      );

      // Sort alphabetically by label
      return accounts.sort((a, b) => {
        const labelA = a.label || "";
        const labelB = b.label || "";

        return labelA.localeCompare(labelB);
      });
    }

    // For non-admin users: Filter accounts based on associated fund's processArea ONLY
    const filteredAccounts = Object.values(accountsSlice.data).filter((account) => {
      // Skip accounts without fund association
      if (!account.fundGlobalId || !fundsSlice.data) return false;

      const associatedFund = fundsSlice.data[account.fundGlobalId];

      // Skip accounts with non-existent associated funds
      if (!associatedFund) return false;

      // For Hybrid users: Filter by active tab
      if (isHybrid) {
        const isInternalTab = activeInfoTab === "Internal Fund Information";

        return isInternalTab
          ? isWireInstructionsInternalFund(associatedFund)
          : isWireInstructionsExternalFund(associatedFund);
      }

      // For Internal only user: Show only accounts associated with internal funds
      if (hasInternal && !hasExternal) {
        return isWireInstructionsInternalFund(associatedFund);
      }

      // For External only user: Show only accounts associated with external funds
      if (!hasInternal && hasExternal) {
        return isWireInstructionsExternalFund(associatedFund);
      }

      // If neither Internal nor External (edge case), don't show any accounts
      return false;
    });

    const accounts = filteredAccounts.map(
      (account): MultiselectOption => ({
        id: account.globalId,
        label: account.name,
        selected: selectedAccountsMap[account.globalId],
      })
    );

    // Sort alphabetically by label
    return accounts.sort((a, b) => {
      const labelA = a.label || "";
      const labelB = b.label || "";

      return labelA.localeCompare(labelB);
    });
  }
);

// Wire Instructions: Filtered funds selector based on user type and active tab
// Uses entitledValidEndDateFundsSelector (filters by valid end dates)
// NO AlpInvest or Real Estate exclusions - show ALL entitled funds with valid end dates
const wireInstructionsFundsSelector = createSelector(
  entitledValidEndDateFundsSelector,
  selectedFundsSelector,
  activeInfoTabSelector,
  (state: RootState) => selectInternalExternalPresence(state as any),
  (entitledFundsSlice, selectedFunds, activeInfoTab, { hasInternal, hasExternal }) => {
    const selectedFundsMap = CommonUtils.convertArrayToMap(selectedFunds);

    if (!entitledFundsSlice.data) return [];

    const isHybrid = hasInternal && hasExternal;

    // Filter funds by processArea (Internal/External) based on user type and active tab
    const filteredFunds = Object.values(entitledFundsSlice.data).filter((fund): fund is NonNullable<typeof fund> => {
      if (!fund) return false;

      // For Hybrid users: Filter funds by active tab
      if (isHybrid) {
        const isInternalTab = activeInfoTab === "Internal Fund Information";

        return isInternalTab ? isWireInstructionsInternalFund(fund) : isWireInstructionsExternalFund(fund);
      }

      // For Internal only user: Show only internal funds
      if (hasInternal && !hasExternal) {
        return isWireInstructionsInternalFund(fund);
      }

      // For External only user: Show only external funds
      if (!hasInternal && hasExternal) {
        return isWireInstructionsExternalFund(fund);
      }

      // Fallback: show all (shouldn't reach here normally)
      return true;
    });

    const funds = filteredFunds
      // Sort alphabetically by fund name
      .sort((a, b) => a.entityName.localeCompare(b.entityName))
      .map((fund) => ({
        id: fund.globalId,
        label: fund.entityName,
        selected: selectedFundsMap[fund.globalId],
      }));

    return funds;
  }
);

// General/NoData: Simple funds selector with NO filtering
// Shows ALL entitled funds (including AlpInvest, expired, Real Estate, etc.)
const simpleRequestFundsSelector = createSelector(
  entitledFundsSelector,
  selectedFundsSelector,
  (fundsSlice, selectedFunds) => {
    const selectedFundsMap = CommonUtils.convertArrayToMap(selectedFunds);

    if (!fundsSlice.data) return [];

    // NO FILTERING - just convert to multiselect options format
    const funds = CommonUtils.convertMapToArray(fundsSlice.data, (fund) => ({
      id: fund.globalId,
      label: fund.entityName,
      selected: selectedFundsMap[fund.globalId],
    }));

    // Sort alphabetically by label
    return funds.sort((a, b) => {
      const labelA = a.label || "";
      const labelB = b.label || "";

      return labelA.localeCompare(labelB);
    });
  }
);

// General/NoData: Simple accounts selector with NO filtering
// Shows ALL entitled accounts (no tab separation, no fund association filtering)
const simpleRequestAccountsSelector = createSelector(
  entitledAccountsSelector,
  selectedAccountsSelector,
  (accountsSlice, selectedAccounts) => {
    const selectedAccountsMap = CommonUtils.convertArrayToMap(selectedAccounts);

    if (!accountsSlice.data) return [];

    // NO FILTERING - just convert to multiselect options format
    const accounts = CommonUtils.convertMapToArray(
      accountsSlice.data,
      (account): MultiselectOption => ({
        id: account.globalId,
        label: account.name,
        selected: selectedAccountsMap[account.globalId],
      })
    );

    // Sort alphabetically by label
    return accounts.sort((a, b) => {
      const labelA = a.label || "";
      const labelB = b.label || "";

      return labelA.localeCompare(labelB);
    });
  }
);

export const selectFieldEditedState = (state: RootState, fieldName: string): boolean =>
  !!state.investorRequest.bankExtraction.extractionEditedFields[fieldName];

export const investorRequestSelector = createSelector(
  investorRequestRootSelector,
  entitledAccountsSelector,
  entitledFundsSelector,
  (investorRequest, accountsSlice, fundsSlice) => ({
    ...investorRequest,
    loading: investorRequest.loading || accountsSlice.loading || fundsSlice.loading,
  })
);

// Main prepared data selector - routes to appropriate selectors based on request type
export const investorRequestPreparedDataSelector = createSelector(
  investorRequestRootSelector,
  wireInstructionsAccountsSelector,
  wireInstructionsFundsSelector,
  simpleRequestAccountsSelector,
  simpleRequestFundsSelector,
  (investorRequest, wireAccounts, wireFunds, simpleAccounts, simpleFunds) => {
    const { requestType, commonFields, customFields } = investorRequest;

    const isExpandedWireEnabled = FeatureToggleService.isFeatureEnabled(ToggledFeature.ExpandedWireInstructions);
    const useWireSelectors = requestType === "update-wire-instructions" && isExpandedWireEnabled;

    return {
      customFields,
      accounts: useWireSelectors ? wireAccounts : simpleAccounts,
      funds: useWireSelectors ? wireFunds : simpleFunds,
      message: commonFields.message,
    };
  }
);

const countriesDataSelector = (state: RootState): Country[] => state.investorRequest.countries.data;

export const countriesOptionsSelector = createSelector(countriesDataSelector, (countries): ListOptionBase[] => {
  return countries
    .filter((country) => country && country.id && country.name)
    .map((country) => ({
      id: String(country.id),
      label: String(country.name),
    }));
});

export const selectedCountrySelector = createSelector(
  investorRequestRootSelector,
  countriesDataSelector,
  (investorRequest, countries): ListOptionBase | undefined => {
    const validCountries = countries.filter((c) => c && c.id && c.name);
    const paymentMethod = investorRequest.customFields.paymentMethod;
    const bankCountry = investorRequest.customFields.bankCountry;

    if (bankCountry) {
      const country = validCountries.find(
        (c) => String(c.id) === String(bankCountry) || String(c.name) === String(bankCountry)
      );

      if (country) {
        return {
          id: String(country.id),
          label: String(country.name),
        };
      }
    }

    // Default to United States for non-Swift payment methods (ACH, FedWire)
    if (paymentMethod !== InvestorRequestPaymentMethod.Swift) {
      const unitedStates = validCountries.find((c) => c.name && String(c.name).toLowerCase() === "united states");

      if (unitedStates) {
        return {
          id: String(unitedStates.id),
          label: String(unitedStates.name),
        };
      }
    }

    return undefined;
  }
);

export const selectedIntermediaryCountrySelector = createSelector(
  investorRequestRootSelector,
  countriesDataSelector,
  (investorRequest, countries): ListOptionBase | undefined => {
    const validCountries = countries.filter((c) => c && c.id && c.name);
    const intermediaryBankCountry = investorRequest.customFields.intermediaryBankCountry;

    if (intermediaryBankCountry) {
      const country = validCountries.find(
        (c) => String(c.id) === String(intermediaryBankCountry) || String(c.name) === String(intermediaryBankCountry)
      );

      if (country) {
        return {
          id: String(country.id),
          label: String(country.name),
        };
      }
    }

    return undefined;
  }
);

// LPC-10289: C2DS validation errors selector
export const c2dsErrorsSelector = (state: RootState): C2DSFieldErrors | null => state.investorRequest.c2dsErrors;

// LPC-10289: Selector to get C2DS error for a specific field
export const c2dsFieldErrorSelector = (state: RootState, fieldKey: C2DSFieldKey) => {
  const errors = state.investorRequest.c2dsErrors;

  return errors ? errors[fieldKey] : null;
};
